import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import {
  type CouponInputFormik,
  couponYupValidation,
  DurationType,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useBoxSubscriptionActions } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency, getCurrencySymbol } from "@modules/currency/currency"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, LoaderCircle } from "lucide-react"
import { useMemo, useState } from "react"

type Props = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity
  setIsOfferDiscountVisible: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferDiscountForm({
  memberId,
  subscription,
  setIsOfferDiscountVisible,
  className,
  ...props
}: Props) {
  const language = useLanguage()
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { isPending, applyDiscount, removeDiscount } = useBoxSubscriptionActions(memberId, subscription.id)

  const discountTypesOptions = [
    { value: CouponType.PERCENT, label: word("box.member.table.offer.discount.percentInputLabel") },
    { value: CouponType.DIRECT, label: word("box.member.table.offer.discount.directInputLabel") },
  ]

  const durationTypesOptions = [
    { value: DurationType.MONTHLY, label: word("box.member.table.offer.discount.monthlyInputLabel") },
    { value: DurationType.UNLIMITED, label: word("box.member.table.offer.discount.unlimitedInputLabel") },
  ]

  const formik = useFormik<CouponInputFormik>({
    initialValues: { type: CouponType.PERCENT, value: 0, durationType: DurationType.UNLIMITED, months: null },
    validationSchema: couponYupValidation({ priceOffer: subscription.priceObject?.value ?? 0 }),
    onSubmit: async () => {
      try {
        setIsSubmitting(true)
        await applyDiscount(formik.values)
        toast.success(word("box.member.table.offer.discount.toastSuccess"), {
          classNames: successToastClassName(),
        })
        setIsResumeVisible(true)
      } catch (e: any) {
        console.error(LoggerType.Service + "update member", e)
        toast.error(word("box.member.table.offer.discount.toastError"), {
          classNames: errorToastClassName(),
        })
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const priceDiscount = useMemo(() => {
    if (formik.values.value > 0 && subscription.priceObject) {
      let priceAfterDiscount = subscription.priceObject.value
      if (formik.values.type === CouponType.PERCENT) {
        const discountPercentage = formik.values.value / 100
        priceAfterDiscount = priceAfterDiscount - priceAfterDiscount * discountPercentage
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (formik.values.type === CouponType.DIRECT) {
        priceAfterDiscount = priceAfterDiscount - formik.values.value
      }
      return priceAfterDiscount
    } else return 0
  }, [formik.values.value, formik.values.type, subscription.priceObject])

  const handleOnEdit = () => {
    setIsResumeVisible(false)
  }

  const handleOnDelete = async () => {
    if (subscription.coupon) {
      await removeDiscount()
        .then(() => {
          setIsResumeVisible(false)
          formik.resetForm()
          toast.success(word("box.member.table.offer.discount.toastDeleteSuccess"), {
            classNames: successToastClassName(),
          })
          setIsOfferDiscountVisible(false)
        })
        .catch((e: any) => {
          console.error(LoggerType.Service + "remove discount", e)
          toast.error(word("box.member.table.offer.discount.toastDeleteError"), {
            classNames: errorToastClassName(),
          })
        })
    }
  }

  return (
    <div className={cn("flex w-full flex-col px-5 pb-6", className)} {...props}>
      {/* HEADER */}
      <div className="flex flex-col gap-6 px-8 py-6">
        <div className="flex">
          <ArrowLeft
            strokeWidth={2.5}
            size={26}
            onClick={() => setIsOfferDiscountVisible(false)}
            className="cursor-pointer"
          />
          <div className="flex grow flex-col items-center justify-center">
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("box.member.table.offer.discount.title")}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-caption">
              {subscription.offer.name}
            </Text>
          </div>
        </div>
      </div>
      {isResumeVisible ? (
        // RESUME
        <div className="mt-6 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Text fontStyle="LS" className="text-textDefault-body">
              {word("box.member.table.offer.discount.supensionTypeLabel")}
            </Text>
            {subscription.priceObject && (
              <Text fontStyle="TM" className="mt-2 text-textDefault-body">
                {getFormattedCouponReduction(
                  formik.values.type,
                  formik.values.value,
                  language,
                  subscription.priceObject.currency,
                )}{" "}
                {word("box.member.table.offer.discount.off")}
              </Text>
            )}
          </div>
          <div className="mt-2 mb-6 flex w-full justify-between px-6">
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("box.member.table.offer.discount.offerPriceLabel")}
              </Text>
              <Text
                fontStyle="TM"
                className="mt-2 text-textError"
                style={{ textDecorationThickness: "1px", textDecorationLine: "line-through" }}
              >
                {formatCurrency(subscription.priceObject?.value ?? 0, subscription.priceObject?.currency, language)}
              </Text>
            </div>

            <div className="flex items-end">
              <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
            </div>

            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
              </Text>
              <Text fontStyle="TM" className="mt-2 text-textSuccess">
                {formatCurrency(priceDiscount, subscription.priceObject?.currency, language)}
              </Text>
            </div>
          </div>
          <div className="flex flex-col">
            <Text fontStyle="TM" className="text-textDefault-body">
              {subscription.coupon?.duration || formik.values.months
                ? word("box.member.table.offer.resume.defineDuration", {
                    count: subscription.coupon?.duration ?? formik.values.months ?? undefined,
                  })
                : word("box.member.table.offer.resume.unlimitedDuration")}
            </Text>
            <Text fontStyle="LM" className="mt-2 text-textDefault-body">
              {` ${word("box.member.table.offer.resume.unlimitedDurationStart")} ${dayjs(
                subscription.coupon?.createdAt,
              ).format("LL")}`}
            </Text>
          </div>

          <div className="mt-20 flex w-full gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={() => void handleOnDelete()}
              disabled={isPending}
              className="grow"
            >
              {word("global.delete")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => void handleOnEdit()}
              disabled={isPending}
              className="grow"
            >
              {word("global.edit")}
            </Button>
          </div>
        </div>
      ) : (
        // FORMIK
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <DropdownInput
              label={word("box.member.table.offer.discount.supensionTypeLabel")}
              options={discountTypesOptions}
              isRequiredInput={true}
              error={getFormErrorMessage("type", formik)}
            >
              <Select
                defaultValue={formik.values.type}
                onValueChange={(value) => {
                  void formik.setFieldValue("type", value)
                }}
                items={discountTypesOptions}
              >
                <SelectTrigger id={"type"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {discountTypesOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DropdownInput>
            <StandardInput
              isRequiredInput
              getFieldProps={formik.getFieldProps}
              name="value"
              error={getFormErrorMessage("value", formik)}
              setFieldValue={formik.setFieldValue}
              containerClassName="flex flex-row items-center"
              inputProps={{
                type: "number",
                inputMode: "numeric",
                max: formik.values.type === CouponType.PERCENT ? 100 : subscription.priceObject?.value,
                step: formik.values.type === CouponType.PERCENT ? 1 : 0.01,
                min: 0,
                disabled: isSubmitting,
                onChange: (e) => {
                  if (formik.values.type === CouponType.PERCENT) {
                    void formik.setFieldValue("value", parseInt(e.target.value))
                  } else {
                    void formik.setFieldValue("value", parseFloat(e.target.value))
                  }
                },
              }}
            >
              {formik.values.type === CouponType.PERCENT ? (
                <Text fontStyle="TS" className="ml-4 flex items-center justify-center text-textDefault-body">
                  %
                </Text>
              ) : (
                <Text fontStyle="TS" className="ml-4 flex items-center justify-center text-textDefault-body">
                  {subscription.referencePrice?.currency
                    ? getCurrencySymbol(subscription.referencePrice.currency, language)
                    : ""}
                </Text>
              )}
            </StandardInput>
          </div>
          <div className="mt-2 mb-6 flex w-full justify-between px-6">
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("box.member.table.offer.discount.offerPriceLabel")}
              </Text>
              <Text
                fontStyle="TM"
                className={cn("mt-2", priceDiscount > 0 ? "text-textError" : "text-textDefault-body")}
                style={priceDiscount > 0 ? { textDecorationThickness: "1px", textDecorationLine: "line-through" } : {}}
              >
                {formatCurrency(subscription.priceObject?.value ?? 0, subscription.priceObject?.currency, language)}
              </Text>
            </div>
            {priceDiscount > 0 && (
              <div className="flex items-end">
                <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
              </div>
            )}
            <div className="flex flex-col items-center">
              <Text fontStyle="LS" className="mt-2 text-textDefault-body">
                {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
              </Text>
              <Text
                fontStyle="TM"
                className={cn("mt-2", priceDiscount > 0 ? "text-textSuccess" : "text-textDefault-body")}
              >
                {priceDiscount >= 0
                  ? formatCurrency(priceDiscount, subscription.priceObject?.currency, language)
                  : "--"}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <DropdownInput
              label={word("box.member.table.offer.discount.durationTypeLabel")}
              options={durationTypesOptions}
              isRequiredInput={true}
              error={getFormErrorMessage("durationType", formik)}
            >
              <Select
                defaultValue={formik.values.durationType}
                onValueChange={(value) => {
                  void formik.setFieldValue("durationType", value)
                  if (value === DurationType.UNLIMITED) {
                    void formik.setFieldValue("months", null)
                  } else {
                    void formik.setFieldValue("months", 1)
                  }
                }}
                items={durationTypesOptions}
              >
                <SelectTrigger id={"durationType"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {durationTypesOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DropdownInput>

            {formik.values.durationType === DurationType.UNLIMITED ? null : (
              <StandardInput
                getFieldProps={formik.getFieldProps}
                name="months"
                error={getFormErrorMessage("months", formik)}
                setFieldValue={formik.setFieldValue}
                containerClassName="flex flex-row items-center"
                inputProps={{
                  type: "number",
                  inputMode: "numeric",
                  step: 1,
                  min: 0,
                  onChange: (e) => {
                    void formik.setFieldValue("months", parseInt(e.target.value))
                  },
                  className: "text-right",
                }}
              >
                <Text
                  fontStyle="TS"
                  className="mr-3 ml-1 flex items-center justify-center text-center text-textDefault-body"
                >
                  {word("global.month.label", { count: formik.values.months ?? 1 })}
                </Text>
              </StandardInput>
            )}
          </div>

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => void formik.submitForm()}
            isLoading={isSubmitting}
            disabled={isPending || isSubmitting || !formik.isValid || !formik.dirty}
          >
            {isPending || (isSubmitting && <LoaderCircle className="animate-spin" />)}
            {word("global.save")}
          </Button>
        </div>
      )}
    </div>
  )
}
